import React, { useState, useEffect } from 'react';
import { Box, Typography, Skeleton, Divider } from '@mui/material';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import { useInView } from 'react-intersection-observer';
import { fetchGameNews, sendNewsEvent } from '../../services/newsApi';
import type { NewsArticle } from '../../types/News';

interface GameNewsSectionProps {
  gameId: number;
}

/**
 * ゲーム詳細ページの関連ニュース。
 *
 * 以前は Google News RSS をリクエスト毎にライブ取得していたため画像が無く、
 * 除外媒体をハードコードで弾いていた。取り込み済みDBを引くだけになり
 * サムネイルも出せるようになっている。
 */
const GameNewsSection: React.FC<GameNewsSectionProps> = ({ gameId }) => {
  const [news, setNews] = useState<NewsArticle[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [failedImages, setFailedImages] = useState<Set<number>>(new Set());
  const { ref, inView } = useInView({ threshold: 0, triggerOnce: true });

  useEffect(() => {
    if (!inView) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const results = await fetchGameNews(gameId);
        if (!cancelled) setNews(results);
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [gameId, inView]);

  const handleImageError = (articleId: number) => {
    setFailedImages((prev) => {
      if (prev.has(articleId)) return prev;
      sendNewsEvent(articleId, 'image_error');
      return new Set(prev).add(articleId);
    });
  };

  // ニュースが無いゲームは大半なので、空なら見出しごと出さない
  if (error || (!loading && news.length === 0)) {
    return <Box ref={ref} />;
  }

  return (
    <Box sx={{ mt: { xs: 3, sm: 3 } }} ref={ref}>
      <Typography variant="h6" sx={{ mb: 1.5 }}>
        関連ニュース
      </Typography>

      <Box
        sx={{
          bgcolor: 'rgba(255, 255, 255, 0.03)',
          borderRadius: 2,
          border: '1px solid rgba(255, 255, 255, 0.08)',
          overflow: 'hidden',
        }}
      >
        {loading ? (
          <Box sx={{ p: 2 }}>
            {[...Array(3)].map((_, i) => (
              <Box key={i} sx={{ mb: 2, display: 'flex', gap: 1.5 }}>
                <Skeleton variant="rectangular" width={96} height={54} sx={{ borderRadius: 1 }} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton width="100%" height={20} />
                  <Skeleton width="40%" height={18} />
                </Box>
              </Box>
            ))}
          </Box>
        ) : (
          news.map((item, index) => (
            <React.Fragment key={item.id}>
              <Box
                component="a"
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => sendNewsEvent(item.id, 'click')}
                sx={{
                  display: 'flex',
                  gap: 1.5,
                  p: 1.5,
                  textDecoration: 'none',
                  color: 'inherit',
                  transition: 'background-color 0.2s ease',
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' },
                }}
              >
                <Box
                  sx={{
                    flexShrink: 0,
                    width: 96,
                    aspectRatio: '16 / 9',
                    borderRadius: 1,
                    overflow: 'hidden',
                    bgcolor: 'rgba(255,255,255,0.05)',
                  }}
                >
                  {item.image && !failedImages.has(item.id) ? (
                    <Box
                      component="img"
                      src={item.image.url}
                      alt=""
                      loading="lazy"
                      referrerPolicy="no-referrer"
                      onError={() => handleImageError(item.id)}
                      sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'rgba(255,255,255,0.25)',
                      }}
                    >
                      <NewspaperIcon fontSize="small" />
                    </Box>
                  )}
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: { xs: '0.7rem', sm: '0.775rem' },
                      fontWeight: 500,
                      lineHeight: 1.4,
                      color: 'rgba(255, 255, 255, 0.87)',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {item.title}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    <Typography sx={{ fontSize: { xs: '0.6rem', sm: '0.65rem' }, color: 'rgba(255,255,255,0.5)' }}>
                      {item.source.name}
                    </Typography>
                    <Typography sx={{ fontSize: { xs: '0.6rem', sm: '0.65rem' }, color: 'rgba(255,255,255,0.35)' }}>
                      {new Date(item.published_at).toLocaleDateString('ja-JP', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                      })}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              {index < news.length - 1 && (
                <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.05)' }} />
              )}
            </React.Fragment>
          ))
        )}
      </Box>
    </Box>
  );
};

export default GameNewsSection;
